/**
 * BuscaCodigoModal — busca manual de código nos catálogos oficiais.
 *
 * Réplica do modal do new_dfd.html: o usuário revisa/corrige o código que a
 * IA sugeriu e preenche o dos itens que ela não encontrou. Sem isto a
 * planilha fica sem saída quando a busca automática erra.
 *
 * As classes de cor são MAPAS ESTÁTICOS, não interpolação: o Tailwind da
 * SPA é build local com purge — `bg-${cor}-600` não sobrevive ao bundle.
 */

import { faSearch, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef, useState } from 'react'
import { buscarCatalogo, type Catalogo, type ItemCatalogo } from '../../api/dfd'

const PALETA: Record<Catalogo, {
  nome: string
  cabecalho: string
  caixa: string
  titulo: string
  texto: string
  botao: string
  foco: string
  destaque: string
}> = {
  catmat: {
    nome: 'CATMAT',
    cabecalho: 'from-blue-600 to-indigo-600',
    caixa: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20',
    titulo: 'text-blue-900 dark:text-blue-200',
    texto: 'text-blue-800 dark:text-blue-300',
    botao: 'bg-blue-600 hover:bg-blue-700',
    foco: 'focus:ring-blue-500',
    destaque: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  },
  catser: {
    nome: 'CATSER',
    cabecalho: 'from-purple-600 to-indigo-600',
    caixa: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20',
    titulo: 'text-purple-900 dark:text-purple-200',
    texto: 'text-purple-800 dark:text-purple-300',
    botao: 'bg-purple-600 hover:bg-purple-700',
    foco: 'focus:ring-purple-500',
    destaque: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  },
  tabela_sus: {
    nome: 'Tabela SUS',
    cabecalho: 'from-teal-600 to-cyan-600',
    caixa: 'border-teal-200 bg-teal-50 dark:border-teal-800 dark:bg-teal-900/20',
    titulo: 'text-teal-900 dark:text-teal-200',
    texto: 'text-teal-800 dark:text-teal-300',
    botao: 'bg-teal-600 hover:bg-teal-700',
    foco: 'focus:ring-teal-500',
    destaque: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  },
}

export function BuscaCodigoModal({
  catalogo,
  descricaoItem,
  codigoAtual,
  aoSelecionar,
  aoFechar,
}: {
  catalogo: Catalogo
  descricaoItem: string
  codigoAtual?: string
  aoSelecionar: (item: ItemCatalogo) => void
  aoFechar: () => void
}) {
  const [termo, setTermo] = useState(descricaoItem)
  // Os resultados guardam o CATÁLOGO que os produziu. Sem isso, uma
  // troca de catálogo com o modal aberto (alternar "usar Tabela SUS")
  // deixava a lista antiga na tela sob a identidade do catálogo novo —
  // e o usuário não tem como saber de onde veio cada código. Num
  // documento oficial, código de catálogo trocado é erro caro.
  const [resultados, setResultados] =
    useState<{ catalogo: Catalogo; itens: ItemCatalogo[] } | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const entrada = useRef<HTMLInputElement>(null)
  const cor = PALETA[catalogo]

  useEffect(() => {
    entrada.current?.focus()
    entrada.current?.select()
  }, [])

  // Trocou o catálogo ou o item? A lista anterior não vale mais.
  useEffect(() => {
    setResultados(null)
    setErro(null)
    setTermo(descricaoItem)
  }, [catalogo, descricaoItem])

  // Esc fecha (o modal do original só fechava no X — aqui é a11y de graça)
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [aoFechar])

  async function buscar() {
    const texto = termo.trim()
    if (!texto) {
      setErro('Digite um termo para buscar.')
      return
    }
    setBuscando(true)
    setErro(null)
    try {
      const resposta = await buscarCatalogo(texto, catalogo)
      // `resposta.catalogo` é o que o servidor DE FATO consultou
      setResultados({ catalogo: resposta.catalogo as Catalogo,
                      itens: resposta.resultados })
    } catch (e) {
      setErro((e as Error).message ?? 'Falha na busca.')
      setResultados(null)
    } finally {
      setBuscando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Busca manual de código ${cor.nome}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar()
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-dark-card">
        <div className={`flex items-center justify-between bg-gradient-to-r px-6 py-4 ${cor.cabecalho}`}>
          <h3 className="text-xl font-bold text-white">
            <FontAwesomeIcon icon={faSearch} className="mr-2" />
            Busca Manual de Código {cor.nome}
          </h3>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar busca"
            className="text-white transition-colors hover:text-slate-200"
          >
            <FontAwesomeIcon icon={faTimes} className="text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className={`mb-4 rounded-lg border p-4 ${cor.caixa}`}>
            <p className={`mb-1 text-sm font-semibold ${cor.titulo}`}>
              Item da Planilha:
            </p>
            <p className={`break-words text-sm ${cor.texto}`}>{descricaoItem}</p>
            {codigoAtual && (
              <p className={`mt-2 text-xs ${cor.texto}`}>
                Código atual: <strong>{codigoAtual}</strong>
              </p>
            )}
          </div>

          <label
            htmlFor="busca-catalogo"
            className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Digite para buscar no {cor.nome}:
          </label>
          <div className="flex gap-2">
            <input
              id="busca-catalogo"
              ref={entrada}
              type="text"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void buscar()
                }
              }}
              placeholder={
                catalogo === 'catmat'
                  ? 'Ex.: Dipirona 500mg, Sabão em pó, 267203…'
                  : 'Ex.: Limpeza, Manutenção…'
              }
              className={`w-full rounded-lg border border-slate-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100 ${cor.foco}`}
            />
            <button
              type="button"
              onClick={() => void buscar()}
              disabled={buscando}
              className={`whitespace-nowrap rounded-lg px-8 py-3 text-lg font-medium text-white transition-colors disabled:opacity-60 ${cor.botao}`}
            >
              <FontAwesomeIcon icon={buscando ? faSpinner : faSearch} spin={buscando} className="mr-2" />
              Buscar
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Também é possível buscar pelo número do código.
          </p>

          {erro && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {erro}
            </div>
          )}

          {resultados && resultados.catalogo === catalogo && (
            <div className="mt-5">
              {/* rótulo GRUDADO no topo da lista: rolando os resultados, o
                  cabeçalho do modal sai de vista e não havia mais como
                  saber de qual catálogo eram aqueles códigos */}
              <p className="sticky top-0 z-10 mb-3 flex items-center gap-2 bg-white py-2 text-sm font-semibold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${cor.destaque}`}>
                  {cor.nome}
                </span>
                {resultados.itens.length === 0
                  ? 'Nenhum resultado encontrado'
                  : `${resultados.itens.length} ${resultados.itens.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}`}
              </p>
              <div className="space-y-2">
                {resultados.itens.map((item) => (
                  <button
                    key={item.codigo}
                    type="button"
                    onClick={() => aoSelecionar(item)}
                    className="flex w-full items-start justify-between gap-4 rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-blue-400 hover:bg-blue-50 dark:border-slate-700 dark:hover:border-blue-500 dark:hover:bg-blue-900/20"
                  >
                    <span className="flex-1">
                      <span className="mb-1 flex items-center gap-2">
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${cor.destaque}`}>
                          {item.codigo}
                        </span>
                        {item.score != null && (
                          <span className="text-xs text-slate-500">
                            Relevância: {item.score.toFixed(0)}
                          </span>
                        )}
                      </span>
                      <span className="mb-1 block break-words text-sm text-slate-700 dark:text-slate-200">
                        {item.descricao}
                      </span>
                      {item.grupo && (
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          <strong>Grupo:</strong> {item.grupo}
                        </span>
                      )}
                      {item.classe && (
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          <strong>Classe:</strong> {item.classe}
                        </span>
                      )}
                    </span>
                    <span className={`whitespace-nowrap rounded px-3 py-1 text-xs font-medium text-white ${cor.botao}`}>
                      Selecionar
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-dark-bg/40">
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-dark-hover"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
